import { useEffect, useRef } from 'react';
import { ChatHubService, type ContactReceivedPayload } from '../api/common/ChatHubService';
import { tokenStore } from '../api/tokenStore';

/**
 * Subscribes to the `OnContactReceived` event pushed by the backend `CrmChatHub`
 * (via `/hubs/chat`). Connects on mount, disconnects on unmount — same lifecycle
 * shape HeartbeatService expects its owner to drive.
 */
export function useContactNotifications(onReceived?: (payload: ContactReceivedPayload) => void) {
  const onReceivedRef = useRef(onReceived);
  onReceivedRef.current = onReceived;

  const serviceRef = useRef<ChatHubService | null>(null);

  useEffect(() => {
    const service = new ChatHubService({
      onAuthTokenRequired: async () => tokenStore.get()?.accessToken ?? null,
    });
    serviceRef.current = service;

    const handler = (payload: ContactReceivedPayload) => onReceivedRef.current?.(payload);
    service.on('OnContactReceived', handler);
    service.connect();

    return () => {
      service.off('OnContactReceived', handler);
      service.disconnect();
      serviceRef.current = null;
    };
  }, []);

  return serviceRef.current;
}
